import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { announcementService } from "@/features/announcements/services/feedService";
import {
  translateText,
  SUPPORTED_LANGUAGES,
  LANGUAGE_FLAGS,
  LANGUAGE_NATIVE_NAMES,
  type LanguageCode,
} from "@/features/chat/services/translationService";
import { Badge } from "@/shared/components/ui/Badge";
import { Button } from "@/shared/components/ui/Button";
import { ProfileAvatar } from "@/shared/components/ui/Avatar";
import { CommentSection } from "@/features/announcements/components/CommentSection";
import { ViewersModal, type Viewer } from "@/features/announcements/components/ViewersModal";
import { ReactorsModal, type Reactor } from "@/features/announcements/components/ReactorsModal";
import { AttachmentPreview } from "@/features/announcements/components/AttachmentPreview";
import { AnnouncementPoll } from "@/features/announcements/components/AnnouncementPoll";
import { formatDate } from "@/shared/utils/formatters";
import type { Announcement } from "@/features/announcements/types/feed";
import { parseAppLink, toAppWebUrl } from "@/shared/utils/appLinks";
import { appLinkLabelFor, useAppLinkLabels } from "@/shared/hooks/useAppLinkLabels";
import { useAppLinkClickHandler } from "@/shared/hooks/useOpenAppLink";


/** Turn plain-text URLs in a string into clickable <a> elements. */
const URL_PATTERN = /((?:https?|hp-chat-v1):\/\/[^\s<]+)/g;
const trimTrailingUrlPunctuation = (url: string): string =>
  url.replace(/[),.;!?]+$/g, "");

/**
 * Our own links are shown by name (`labels`, from useAppLinkLabels) and opened
 * inside the app by `onLinkClick`; every other link opens in a new tab.
 */
const linkifyText = (
  text: string,
  labels: Record<string, string>,
  onLinkClick: (e: ReactMouseEvent<HTMLAnchorElement>, href: string) => void,
): ReactNode[] => {
  const parts = text.split(URL_PATTERN);
  return parts.map((part, i) => {
    if (!/^(?:https?|hp-chat-v1):\/\//i.test(part)) {
      return <span key={i}>{part}</span>;
    }

    const rawHref = trimTrailingUrlPunctuation(part);
    const trailingText = part.slice(rawHref.length);
    const isAppLink = parseAppLink(rawHref) !== null;
    // A browser can't open hp-chat-v1://, so point it at the web URL.
    const href = isAppLink ? toAppWebUrl(rawHref) : rawHref;
    if (!isAppLink && !/^https?:/i.test(rawHref)) return <span key={i}>{part}</span>;

    return (
      <span key={i}>
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => onLinkClick(e, href)}
          className={`text-indigo-600 hover:text-indigo-800 underline ${isAppLink ? "break-words" : "break-all"}`}
        >
          {(isAppLink && appLinkLabelFor(labels, rawHref)) || rawHref}
        </a>
        {trailingText}
      </span>
    );
  });
};

const getYouTubeVideoId = (rawUrl: string): string | null => {
  try {
    const url = new URL(trimTrailingUrlPunctuation(rawUrl));
    const hostname = url.hostname.replace(/^www\./, "").toLowerCase();

    if (hostname === "youtu.be") {
      return url.pathname.split("/").filter(Boolean)[0] ?? null;
    }

    if (!hostname.endsWith("youtube.com") && !hostname.endsWith("youtube-nocookie.com")) {
      return null;
    }

    if (url.pathname === "/watch") {
      return url.searchParams.get("v");
    }

    const parts = url.pathname.split("/").filter(Boolean);
    if (["embed", "shorts", "live"].includes(parts[0])) {
      return parts[1] ?? null;
    }

    return null;
  } catch {
    return null;
  }
};

const getYouTubeLinks = (text: string) => {
  const urls = text.match(URL_PATTERN) ?? [];
  const seen = new Set<string>();

  return urls.flatMap((url) => {
    const href = trimTrailingUrlPunctuation(url);
    const videoId = getYouTubeVideoId(href);
    if (!videoId || seen.has(videoId)) return [];
    seen.add(videoId);
    return [{ href, videoId }];
  });
};

const YouTubePreview = ({
  videoId,
}: {
  videoId: string;
}) => {
  const [playing, setPlaying] = useState(false);

  if (playing) {
    return (
      <div className="shrink-0">
        <div className="w-[360px] h-[240px] min-w-[240px] min-h-[180px] max-w-[80vw] max-h-[70vh] resize overflow-hidden rounded-lg border border-gray-200 bg-black">
          <iframe
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
            title="YouTube video player"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            className="h-full w-full bg-black"
          />
        </div>
        <p className="text-xs text-gray-600 mt-1 truncate w-[240px]">
          YouTube video
        </p>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setPlaying(true)}
      className="group block shrink-0 text-left"
    >
      <div className="relative w-[240px] h-[180px] overflow-hidden rounded-lg border border-gray-200 bg-gray-900 group-hover:border-indigo-300 transition-colors">
        <img
          src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`}
          alt="YouTube video thumbnail"
          loading="lazy"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 flex items-center justify-center bg-black/10 group-hover:bg-black/20 transition-colors">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-red-600 text-white shadow-lg">
            <svg className="h-6 w-6 translate-x-0.5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z" />
            </svg>
          </span>
        </div>
      </div>
      <p className="text-xs text-gray-600 mt-1 truncate w-[240px]">
        YouTube video
      </p>
    </button>
  );
};

const REACTION_EMOJIS = ["👍", "❤️", "😂", "😮", "🎉"];

const COUNTRY_OPTIONS = [
  { code: "JP", key: "country_jp" },
  { code: "PH", key: "country_ph" },
  { code: "VN", key: "country_vn" },
  { code: "ID", key: "country_id" },
  { code: "MM", key: "country_mm" },
  { code: "KH", key: "country_kh" },
  { code: "TH", key: "country_th" },
  { code: "CN", key: "country_cn" },
  { code: "NP", key: "country_np" },
  { code: "IN", key: "country_in" },
  { code: "BD", key: "country_bd" },
  { code: "LK", key: "country_lk" },
];

/** Track which announcements this session has already marked as seen. */
const seenSet = new Set<number>();

/** Normalise reactions — backend may return:
 *  • { "🔥": ["uid1"], "😄": ["uid2"] }           (direct map)
 *  • { reactions: { "🔥": ["uid1"] } }             (wrapped)
 *  • { data: { reactions: { … } } }                (double-wrapped)
 *  We keep the string[] form so we can highlight the current user's reactions. */
const normalizeReactions = (raw: unknown): Record<string, string[]> => {
  if (!raw || typeof raw !== "object") return {};

  // Unwrap common wrappers: .reactions or .data.reactions
  let obj = raw as Record<string, unknown>;
  if (
    "reactions" in obj &&
    obj.reactions &&
    typeof obj.reactions === "object"
  ) {
    obj = obj.reactions as Record<string, unknown>;
  } else if ("data" in obj && obj.data && typeof obj.data === "object") {
    const inner = obj.data as Record<string, unknown>;
    if (
      "reactions" in inner &&
      inner.reactions &&
      typeof inner.reactions === "object"
    ) {
      obj = inner.reactions as Record<string, unknown>;
    }
  }

  const formatted: Record<string, string[]> = {};
  Object.entries(obj).forEach(([emoji, value]) => {
    if (Array.isArray(value)) {
      formatted[emoji] = value.map(String);
    }
    // If numeric we can't recover user-ids — skip
  });
  return formatted;
};

interface AnnouncementCardProps {
  announcement: Announcement;
  isOfficer: boolean;
  currentUserId: string | number;
  currentUserName?: string;
  onEdit: (announcement: Announcement) => void;
  onFavoriteToggle?: (rowId: number, isFavorited: boolean) => void;
  isFocused?: boolean;
  onPollChange?: (poll: NonNullable<Announcement["poll"]>) => void;
}

/** Survey band status: lock and close beat the countdown. */
const pollStatusLabel = (poll: NonNullable<Announcement["poll"]>, t: TFunction) => {
  if (poll.is_locked) return t("feed.poll.locked");
  if (!poll.is_open) return t("feed.poll.closed");
  if (poll.closes_at) {
    const closesAt = new Date(poll.closes_at);
    const now = new Date();
    if (closesAt <= now) return t("feed.poll.closed");
    const days = Math.ceil((closesAt.getTime() - now.getTime()) / 86_400_000);
    return closesAt.toDateString() === now.toDateString() ? t("feed.poll.closes_today") : t("feed.poll.closes_in", { count: days });
  }
  return t("feed.poll.open");
};

export const AnnouncementCard = ({
  announcement,
  isOfficer,
  currentUserId,
  currentUserName,
  onEdit,
  onFavoriteToggle,
  onPollChange,
  isFocused = false,
}: AnnouncementCardProps) => {
  const { t } = useTranslation();
  const [showReactions, setShowReactions] = useState(false);
  const [reactingEmoji, setReactingEmoji] = useState<string | null>(null);
  const [reactions, setReactions] = useState<Record<string, string[]>>(() =>
    normalizeReactions(announcement.reactions),
  );
  const [viewers, setViewers] = useState<Viewer[]>([]);
  const [loadingViewers, setLoadingViewers] = useState(false);
  const [showViewers, setShowViewers] = useState(false);
  const [reactorsEmoji, setReactorsEmoji] = useState<string | null>(null);
  const [reactors, setReactors] = useState<Reactor[]>([]);
  const [loadingReactors, setLoadingReactors] = useState<string | null>(null);
  const [showComments, setShowComments] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [commentCount, setCommentCount] = useState<number>(
    announcement.comments_count ?? 0,
  );
  const [viewCount, setViewCount] = useState<number>(
    announcement.view_count ?? 0,
  );
  const [highlight, setHighlight] = useState(isFocused);
  const [isFavorited, setIsFavorited] = useState(announcement.is_favorited ?? false);
  const [favoriting, setFavoriting] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  // Remove highlight after animation
  useEffect(() => {
    if (!isFocused) return;
    setHighlight(true);
    const timer = setTimeout(() => setHighlight(false), 2500);
    return () => clearTimeout(timer);
  }, [isFocused]);

  // Translation state
  const [translatedTitle, setTranslatedTitle] = useState<string | null>(null);
  const [translatedBody, setTranslatedBody] = useState<string | null>(null);
  const [bodyTranslating, setBodyTranslating] = useState(false);
  const [activeLang, setActiveLang] = useState<LanguageCode | null>(null);
  const [langPickerOpen, setLangPickerOpen] = useState(false);
  const langPickerRef = useRef<HTMLDivElement>(null);

  // Close language picker when clicking outside
  useEffect(() => {
    if (!langPickerOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (langPickerRef.current && !langPickerRef.current.contains(e.target as Node)) {
        setLangPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [langPickerOpen]);

  const handleBodyTranslate = async (targetLang: LanguageCode) => {
    setLangPickerOpen(false);
    setBodyTranslating(true);
    try {
      const bodyText = announcement.content_text || announcement.content || "";
      const titleText = announcement.title || "";
      const [bodyResult, titleResult] = await Promise.all([
        translateText(bodyText, targetLang),
        titleText ? translateText(titleText, targetLang) : Promise.resolve(null),
      ]);
      if (bodyResult.success) {
        setTranslatedBody(bodyResult.translatedText);
      } else {
        console.error("Body translation failed:", bodyResult.error);
      }
      if (titleResult?.success) {
        setTranslatedTitle(titleResult.translatedText);
      } else if (titleResult) {
        console.error("Title translation failed:", titleResult.error);
      }
      setActiveLang(targetLang);
    } catch (err) {
      console.error("Translation error:", err);
    } finally {
      setBodyTranslating(false);
    }
  };

  // Resolve fields — backend may use different names
  const bodyText = announcement.content_text || announcement.content || "";
  const authorName =
    announcement.created_by_name || announcement.author_name || "";
  const companies = announcement.target_companies || announcement.company || [];

  const userIdStr = String(currentUserId);

  // Mark as seen when the card scrolls into view (once per session per announcement)
  useEffect(() => {
    const el = cardRef.current;
    if (!el || seenSet.has(announcement.row_id)) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.disconnect();
          seenSet.add(announcement.row_id);
          announcementService
            .markAsSeen(announcement.row_id)
            .catch(console.error);
        }
      },
      { threshold: 0.3 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [announcement.row_id]);

  const handleCopyLink = async () => {
    const url = `${window.location.origin}/announcement/${announcement.row_id}`;
    await navigator.clipboard.writeText(url);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const handleFavorite = async () => {
    const next = !isFavorited;
    setIsFavorited(next);
    setFavoriting(true);
    try {
      await announcementService.toggleFavorite(announcement.row_id);
      onFavoriteToggle?.(announcement.row_id, next);
    } catch (err) {
      console.error("Failed to toggle favorite:", err);
      setIsFavorited(!next);
    } finally {
      setFavoriting(false);
    }
  };

  const handleReaction = async (emoji: string) => {
    setReactingEmoji(emoji);

    // Optimistic update — one reaction per user; remove old, toggle new
    setReactions((prev) => {
      const next: Record<string, string[]> = {};

      // Remove current user from every emoji
      for (const [e, users] of Object.entries(prev)) {
        const filtered = users.filter((id) => id !== userIdStr);
        if (filtered.length > 0) next[e] = filtered;
      }

      // If user clicked the same emoji they already had, it's just a remove (toggle off)
      const hadThisEmoji = (prev[emoji] ?? []).includes(userIdStr);
      if (!hadThisEmoji) {
        next[emoji] = [...(next[emoji] ?? []), userIdStr];
      }

      return next;
    });

    try {
      await announcementService.toggleReaction(announcement.row_id, emoji);
    } catch (err) {
      console.error("Failed to toggle reaction:", err);
      // Revert to prop data on failure
      setReactions(normalizeReactions(announcement.reactions));
    } finally {
      setReactingEmoji(null);
      setShowReactions(false);
    }
  };

  const handleShowReactors = async (emoji: string) => {
    setLoadingReactors(emoji);
    try {
      // API returns: [{ emoji: "👍", users: [{ id, name, company }] }, ...]
      const data = await announcementService.getReactions(announcement.row_id);
      let list: Reactor[] = [];
      if (Array.isArray(data)) {
        const entry = data.find((g: Record<string, unknown>) => g.emoji === emoji);
        if (entry && Array.isArray(entry.users)) {
          list = entry.users.map((u: Record<string, unknown>) => ({
            name: String(u.name ?? ""),
            company: u.company ? String(u.company) : undefined,
          }));
        }
      }
      setReactors(list);
      setReactorsEmoji(emoji);
    } catch (err) {
      console.error("Failed to load reactors:", err);
    } finally {
      setLoadingReactors(null);
    }
  };

  const handleViewSeen = async () => {
    setLoadingViewers(true);
    try {
      const data = await announcementService.getViewers(announcement.row_id);
      setViewers(data);
      setViewCount(data.length);
      setShowViewers(true);
    } catch (err) {
      console.error("Failed to load viewers:", err);
    } finally {
      setLoadingViewers(false);
    }
  };

  const reactionEntries = Object.entries(reactions).filter(
    ([, users]) => users.length > 0,
  );
  const totalReactions = reactionEntries.reduce(
    (sum, [, users]) => sum + users.length,
    0,
  );
  const hasDateRange = announcement.date_from || announcement.date_to;

  // Content expand/collapse
  const CONTENT_MAX_LENGTH = 300;
  const isLongContent = bodyText.length > CONTENT_MAX_LENGTH;
  const displayText =
    isLongContent && !expanded
      ? bodyText.slice(0, CONTENT_MAX_LENGTH) + "…"
      : bodyText;
  const youtubeLinks = getYouTubeLinks(bodyText);
  const appLinkLabels = useAppLinkLabels(translatedBody ?? bodyText);
  const handleLinkClick = useAppLinkClickHandler();

  return (
    <div
      id={`announcement-${announcement.row_id}`}
      ref={cardRef}
      className={`bg-white rounded-xl border shadow-sm hover:shadow-md transition-all ${
        highlight
          ? "border-indigo-400 ring-2 ring-indigo-300 animate-pulse"
          : "border-gray-200"
      }`}
      style={
        highlight
          ? { animationDuration: "1.5s", animationIterationCount: "2" }
          : undefined
      }
    >
      {/* Top accent — a survey gets a labelled band instead of the thin bar */}
      {announcement.poll ? (
        <div className="flex items-center justify-between gap-2 rounded-t-xl bg-violet-50 px-4 py-2 text-xs md:px-5">
          <span className="flex items-center gap-1.5 font-semibold text-violet-800">
            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 20V10m6 10V4m6 16v-7m4 7H2" /></svg>
            {t("feed.poll.survey")}
            {announcement.poll.total_respondents !== undefined && (
              <span className="font-normal text-violet-600">· {t("feed.poll.responses", { count: announcement.poll.total_respondents })}</span>
            )}
          </span>
          <span className="text-violet-600">{pollStatusLabel(announcement.poll, t)}</span>
        </div>
      ) : (
        <div className="h-1 bg-indigo-500 rounded-t-xl" />
      )}

      <div className="p-4 md:p-5">
        {/* ── Header ── */}
        <div className="flex items-start justify-between gap-2 md:gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="shrink-0">
                <ProfileAvatar
                  picId={announcement.author_profile_pic_id}
                  firstName={authorName.split(" ")[0]}
                  lastName={authorName.split(" ").slice(1).join(" ")}
                  size="sm"
                />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {authorName || t("feed.unknown_author")}
                </p>
                <p className="text-xs text-gray-500">
                  {formatDate(announcement.created_at)}
                </p>
              </div>
            </div>

            {/* ── Metadata pills ── */}
            <div className="flex flex-wrap items-center gap-2 mt-4">
              {companies.map((c: any, i: number) => {
                const label =
                  typeof c === "string" ? c : c?.label || c?.company_name || "";
                const key =
                  typeof c === "string" ? c : c?.value || c?.company_id || i;
                return (
                  <Badge key={key} variant="default">
                    {label}
                  </Badge>
                );
              })}

              {announcement.batch_no && (
                <Badge variant="warning">{announcement.batch_no}</Badge>
              )}

              {(announcement.country ?? []).map((code: string) => {
                const opt = COUNTRY_OPTIONS.find((c) => c.code === code);
                return (
                  <Badge key={code} variant="info">
                    {opt ? t(`profile.${opt.key}`) : code}
                  </Badge>
                );
              })}

              {announcement.sending_org && (
                <Badge variant="success">
                  {announcement.sending_org.length > 15
                    ? announcement.sending_org.slice(0, 15) + "…"
                    : announcement.sending_org}
                </Badge>
              )}
            </div>

            {announcement.poll ? (
              <>
                <h2 className="mt-3 text-base font-semibold text-gray-900 md:text-lg">{announcement.poll.question}</h2>
                <p className="mt-0.5 text-sm text-gray-500">{translatedTitle || announcement.title}</p>
              </>
            ) : (
              <h2 className="text-base md:text-lg font-semibold text-gray-900 mt-3">
                {translatedTitle || announcement.title}
              </h2>
            )}
          </div>

          <div className="flex items-center gap-1 shrink-0">
            {/* Language picker — globe when idle, lang code when active */}
            <div className="relative" ref={langPickerRef}>
              <button
                onClick={() => {
                  if (translatedBody || translatedTitle) {
                    setTranslatedBody(null);
                    setTranslatedTitle(null);
                    setActiveLang(null);
                  } else {
                    setLangPickerOpen((prev) => !prev);
                  }
                }}
                disabled={bodyTranslating}
                title={
                  translatedBody || translatedTitle
                    ? t("feed.show_original")
                    : t("feed.translate")
                }
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors disabled:opacity-50 ${
                  translatedBody || translatedTitle
                    ? "bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
                    : "text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                }`}
              >
                {bodyTranslating ? (
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : (
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                    />
                  </svg>
                )}
                {activeLang && !bodyTranslating && (
                  <span className="font-semibold">{activeLang.toUpperCase()}</span>
                )}
              </button>

              {/* Language dropdown */}
              {langPickerOpen && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-20 min-w-[200px] py-1 overflow-y-auto max-h-[60vh]">
                  {SUPPORTED_LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleBodyTranslate(lang.code as LanguageCode)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <span className="text-base">{LANGUAGE_FLAGS[lang.code] || ""}</span>
                      <span>{LANGUAGE_NATIVE_NAMES[lang.code] || lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {isOfficer && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEdit(announcement)}
              >
                {t("common.edit")}
              </Button>
            )}
          </div>
        </div>

        {/* ── Body text ── */}
        {bodyText ? (
          <div className="mt-2">
            <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
              {linkifyText(translatedBody ?? displayText, appLinkLabels, handleLinkClick)}
            </p>
            {translatedBody && (
              <p className="text-[10px] text-indigo-400 italic mt-0.5">
                {t("feed.translated")}
              </p>
            )}
            {isLongContent && !translatedBody && (
              <div className="mt-2">
                <button
                  onClick={() => setExpanded(!expanded)}
                  className="text-sm md:text-xs py-1 text-indigo-600 hover:text-indigo-800 font-medium"
                >
                  {expanded
                    ? t("common.show_less", "Show less")
                    : t("common.show_more", "Show more")}
                </button>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-400 italic mt-2">
            {t("feed.no_content")}
          </p>
        )}

        {/* ── Attachment previews ── */}
        {announcement.attachments && announcement.attachments.length > 0 && (
          <AttachmentPreview attachments={announcement.attachments} />
        )}

        {youtubeLinks.length > 0 && (
          <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
            {youtubeLinks.map((link) => (
              <YouTubePreview
                key={link.videoId}
                videoId={link.videoId}
              />
            ))}
          </div>
        )}

        {announcement.poll && (
          <AnnouncementPoll rowId={announcement.row_id} initialPoll={announcement.poll} isOfficer={isOfficer} onPollChange={onPollChange} />
        )}

        {/* ── Date range ── */}
        {hasDateRange && (
          <div className="flex items-center gap-2 mt-3 text-xs text-gray-500">
            <svg
              className="w-3.5 h-3.5 text-gray-400 shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <span>
              {announcement.date_from
                ? formatDate(announcement.date_from)
                : "—"}
              {" → "}
              {announcement.date_to
                ? formatDate(announcement.date_to)
                : t("feed.ongoing")}
            </span>
          </div>
        )}

        {/* ── Reactions — always visible ── */}
        <div className="flex flex-wrap items-center gap-2 mt-4">
          {reactionEntries.map(([emoji, users]) => {
            const reacted = users.includes(userIdStr);
            return (
              <button
                key={emoji}
                onClick={() => handleShowReactors(emoji)}
                disabled={loadingReactors === emoji}
                className={`inline-flex items-center gap-1 px-3 py-1.5 md:px-2.5 md:py-1 rounded-full text-xs transition-colors border active:scale-95 disabled:opacity-60 ${
                  reacted
                    ? "bg-indigo-50 border-indigo-300 text-indigo-700"
                    : "bg-gray-50 hover:bg-indigo-50 border-gray-200 hover:border-indigo-200"
                }`}
              >
                <span>{emoji}</span>
                <span className="font-medium">{users.length}</span>
              </button>
            );
          })}
          {reactionEntries.length === 0 && (
            <span className="text-xs text-gray-400">
              {t("feed.no_reactions")}
            </span>
          )}
        </div>

        {/* ── Action bar ── */}
        <div className="flex flex-wrap items-center gap-1 md:gap-2 mt-4 pt-3 border-t border-gray-100">
          {/* Favorite */}
          <button
            onClick={handleFavorite}
            disabled={favoriting}
            title={isFavorited ? t("feed.unfavorite") : t("feed.favorite")}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors disabled:opacity-50 ${
              isFavorited
                ? "text-amber-500 hover:text-amber-600"
                : "text-gray-400 hover:text-amber-400"
            }`}
          >
            <svg
              className="w-4 h-4"
              viewBox="0 0 24 24"
              fill={isFavorited ? "currentColor" : "none"}
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
              />
            </svg>
          </button>

          {/* Copy link */}
          <button
            onClick={handleCopyLink}
            title="Copy link"
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              linkCopied
                ? "text-blue-500"
                : "text-gray-400 hover:text-gray-600"
            }`}
          >
            {linkCopied ? (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            )}
          </button>

          {/* React button */}
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowReactions(!showReactions)}
            >
              <span className="mr-1.5">😊</span>
              {t("feed.react")}
              {totalReactions > 0 && (
                <span className="ml-1.5 text-gray-400">{totalReactions}</span>
              )}
            </Button>
            {showReactions && (
              <div className="absolute bottom-full left-0 mb-1 flex gap-1 bg-white border border-gray-200 rounded-lg shadow-lg p-1.5 md:p-2 z-10">
                {REACTION_EMOJIS.map((emoji) => (
                  <button
                    key={emoji}
                    onClick={() => handleReaction(emoji)}
                    disabled={reactingEmoji === emoji}
                    className="text-xl md:text-lg hover:scale-125 transition-transform disabled:opacity-50 p-2 md:p-1 active:scale-95"
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Comments toggle */}
          {announcement.comments_on && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowComments(!showComments)}
            >
              <svg
                className="w-4 h-4 mr-1.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                />
              </svg>
              {t("feed.comments")}
              {commentCount > 0 && (
                <span className="ml-1.5 text-gray-400">{commentCount}</span>
              )}
            </Button>
          )}

          {/* Seen-by (officer) */}
          {isOfficer && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleViewSeen}
              loading={loadingViewers}
            >
              <svg
                className="w-4 h-4 mr-1.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
              {t("feed.seen_by")}
              {viewCount > 0 && (
                <span className="ml-1.5 text-gray-400">{viewCount}</span>
              )}
            </Button>
          )}

        </div>

        {/* ── Viewers modal ── */}
        <ViewersModal
          isOpen={showViewers}
          onClose={() => setShowViewers(false)}
          viewers={viewers}
        />

        {/* ── Reactors modal ── */}
        <ReactorsModal
          isOpen={reactorsEmoji !== null}
          onClose={() => setReactorsEmoji(null)}
          emoji={reactorsEmoji ?? ""}
          reactors={reactors}
        />

        {/* ── Comments section (inline) ── */}
        {showComments && announcement.comments_on && (
          <CommentSection
            announcementId={announcement.row_id}
            currentUserId={currentUserId}
            currentUserName={currentUserName}
            isOfficer={isOfficer}
            onCountChange={setCommentCount}
          />
        )}
      </div>
    </div>
  );
};
